import type { NitroFetchOptions } from "nitropack";
import type { MaybeRef } from "vue";
import type { ProductItem } from "../types";
import type { UseApiFetchOptions, UseApiFetchResponse } from "../composables/useApiFetch";
import {
  $apiFetch,
  computed,
  unref,
  useApiFetch,
} from "#imports";

export function useProductResource () {
  /**
   * Get all products.
   * @return A list of products.
   */
  function list<T = Record<string, never>> (): UseApiFetchResponse<ProductItem<T>[]> {
    const computedUrl = computed( () => {
      let url_ = "/api/products";

      url_ = url_.replace( /[?&]$/, "" );
      return url_;
    } );

    const options_: UseApiFetchOptions<ProductItem<T>[]> = {
      method: "GET",
      watch: [computedUrl],
    };

    return useApiFetch<ProductItem<T>[]>( computedUrl, options_ );
  }

  /**
   * Retrieves a list of product representations based on the provided list of product keys.
   * @param keys A list of GUIDs representing the keys of the products to be retrieved.
   * @return A task that represents the asynchronous operation. The task result contains a list of product representations.
   */
  function listByKeys<T = Record<string, never>> (
    keys: MaybeRef<string[]>,
  ): UseApiFetchResponse<ProductItem<T>[]> {
    const computedUrl = computed( () => {
      let url_ = "/api/products/keys";

      url_ = url_.replace( /[?&]$/, "" );
      return url_;
    } );

    const options_: UseApiFetchOptions<ProductItem<T>[]> = {
      body: keys,
      method: "GET",
      watch: [computedUrl],
    };

    return useApiFetch<ProductItem<T>[]>( computedUrl, options_ );
  }

  /**
   * Retrieves a list of product representations by their IDs.
   * @param ids A list of product IDs used to retrieve associated products.
   * @return A task that represents the asynchronous operation, containing a list of product representations.
   */
  function listByIds<T = Record<string, never>> (
    ids: MaybeRef<number[]>,
  ): UseApiFetchResponse<ProductItem<T>[]> {
    const computedUrl = computed( () => {
      let url_ = "/api/products/ids";

      url_ = url_.replace( /[?&]$/, "" );
      return url_;
    } );

    const options_: UseApiFetchOptions<ProductItem<T>[]> = {
      body: ids,
      method: "GET",
      watch: [computedUrl],
    };

    return useApiFetch<ProductItem<T>[]>( computedUrl, options_ );
  }

  /**
   * Get a product by ID
   * @param id The product ID
   * @return The matching product
   */
  function getById<T = Record<string, never>> (
    id: MaybeRef<number>,
  ): UseApiFetchResponse<ProductItem<T>> {
    const computedUrl = computed( () => {
      let url_ = "/api/products/{id}";

      const idUnref = unref( id );
      if ( idUnref === undefined || idUnref === null )
        throw new Error( "The parameter 'id' must be defined." );
      url_ = url_.replace( "{id}", encodeURIComponent( "" + idUnref ) );

      url_ = url_.replace( /[?&]$/, "" );
      return url_;
    } );

    const options_: UseApiFetchOptions<ProductItem<T>> = {
      method: "GET",
      watch: [computedUrl],
    };

    return useApiFetch<ProductItem<T>>( computedUrl, options_ );
  }

  /**
   * Get a product by key
   * @param key The product key
   * @return The matching product
   */
  function getByKey<T = Record<string, never>> (
    key: MaybeRef<string>,
  ): UseApiFetchResponse<ProductItem<T>> {
    const computedUrl = computed( () => {
      let url_ = "/api/products/{key}";

      const keyUnref = unref( key );
      if ( keyUnref === undefined || keyUnref === null )
        throw new Error( "The parameter 'key' must be defined." );
      url_ = url_.replace( "{key}", encodeURIComponent( "" + keyUnref ) );

      url_ = url_.replace( /[?&]$/, "" );
      return url_;
    } );

    const options_: UseApiFetchOptions<ProductItem<T>> = {
      method: "GET",
      watch: [computedUrl],
    };

    return useApiFetch<ProductItem<T>>( computedUrl, options_ );
  }

  /**
   * Get a product by code
   * @param code The product code
   * @return The matching product
   */
  function getByCode<T = Record<string, never>> (
    code: MaybeRef<string>,
  ): UseApiFetchResponse<ProductItem<T>> {
    const computedUrl = computed( () => {
      let url_ = "/api/products/code/{code}";

      const codeUnref = unref( code );
      if ( codeUnref === undefined || codeUnref === null )
        throw new Error( "The parameter 'code' must be defined." );
      url_ = url_.replace( "{code}", encodeURIComponent( "" + codeUnref ) );

      url_ = url_.replace( /[?&]$/, "" );
      return url_;
    } );

    const options_: UseApiFetchOptions<ProductItem<T>> = {
      method: "GET",
      watch: [computedUrl],
    };

    return useApiFetch<ProductItem<T>>( computedUrl, options_ );
  }

  /**
   * Get a product by slug
   * @param slug The product slug
   * @return The matching product
   */
  function getBySlug<T = Record<string, never>> (
    slug: MaybeRef<string>,
  ): UseApiFetchResponse<ProductItem<T>> {
    const computedUrl = computed( () => {
      let url_ = "/api/products/slug/{slug}";

      const slugUnref = unref( slug );
      if ( slugUnref === undefined || slugUnref === null )
        throw new Error( "The parameter 'slug' must be defined." );
      url_ = url_.replace( "{slug}", encodeURIComponent( "" + slugUnref ) );

      url_ = url_.replace( /[?&]$/, "" );
      return url_;
    } );

    const options_: UseApiFetchOptions<ProductItem<T>> = {
      method: "GET",
      watch: [computedUrl],
    };

    return useApiFetch<ProductItem<T>>( computedUrl, options_ );
  }

  return {
    list,
    listByKeys,
    listByIds,
    getById,
    getByKey,
    getByCode,
    getBySlug,
  };
}

export function $productResource () {
  const dependencies_ = useFetchDependencies();

  /**
   * Get all products.
   * @return A list of products.
   */
  function list<T = Record<string, never>> () {
    let url_ = "/api/products";

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
    };

    return $apiFetch<ProductItem<T>[]>( url_, options_, dependencies_ );
  }

  /**
   * Retrieves a list of product representations based on the provided list of product keys.
   * @param keys A list of GUIDs representing the keys of the products to be retrieved.
   * @return A task that represents the asynchronous operation. The task result contains a list of product representations.
   */
  function listByKeys<T = Record<string, never>> (
    keys: string[],
  ) {
    let url_ = "/api/products/keys";

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      body: keys,
      method: "GET",
    };

    return $apiFetch<ProductItem<T>[]>( url_, options_, dependencies_ );
  }

  /**
   * Retrieves a list of product representations by their IDs.
   * @param ids A list of product IDs used to retrieve associated products.
   * @return A task that represents the asynchronous operation, containing a list of product representations.
   */
  function listByIds<T = Record<string, never>> (
    ids: number[],
  ) {
    let url_ = "/api/products/ids";

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      body: ids,
      method: "GET",
    };

    return $apiFetch<ProductItem<T>[]>( url_, options_, dependencies_ );
  }

  /**
   * Get a product by ID
   * @param id The product ID
   * @return The matching product
   */
  function getById<T = Record<string, never>> (
    id: number,
  ) {
    let url_ = "/api/products/{id}";

    const idUnref = unref( id );
    if ( idUnref === undefined || idUnref === null )
      throw new Error( "The parameter 'id' must be defined." );
    url_ = url_.replace( "{id}", encodeURIComponent( "" + idUnref ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
    };

    return $apiFetch<ProductItem<T>>( url_, options_, dependencies_ );
  }

  /**
   * Get a product by key
   * @param key The product key
   * @return The matching product
   */
  function getByKey<T = Record<string, never>> (
    key: string,
  ) {
    let url_ = "/api/products/{key}";

    const keyUnref = unref( key );
    if ( keyUnref === undefined || keyUnref === null )
      throw new Error( "The parameter 'key' must be defined." );
    url_ = url_.replace( "{key}", encodeURIComponent( "" + keyUnref ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
    };

    return $apiFetch<ProductItem<T>>( url_, options_, dependencies_ );
  }

  /**
   * Get a product by code
   * @param code The product code
   * @return The matching product
   */
  function getByCode<T = Record<string, never>> (
    code: string,
  ) {
    let url_ = "/api/products/code/{code}";

    const codeUnref = unref( code );
    if ( codeUnref === undefined || codeUnref === null )
      throw new Error( "The parameter 'code' must be defined." );
    url_ = url_.replace( "{code}", encodeURIComponent( "" + codeUnref ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
    };

    return $apiFetch<ProductItem<T>>( url_, options_, dependencies_ );
  }

  /**
   * Get a product by slug
   * @param slug The product slug
   * @return The matching product
   */
  function getBySlug<T = Record<string, never>> (
    slug: string,
  ) {
    let url_ = "/api/products/slug/{slug}";

    const slugUnref = unref( slug );
    if ( slugUnref === undefined || slugUnref === null )
      throw new Error( "The parameter 'slug' must be defined." );
    url_ = url_.replace( "{slug}", encodeURIComponent( "" + slugUnref ) );

    url_ = url_.replace( /[?&]$/, "" );

    const options_: NitroFetchOptions<string> = {
      method: "GET",
    };

    return $apiFetch<ProductItem<T>>( url_, options_, dependencies_ );
  }

  return {
    list,
    listByKeys,
    listByIds,
    getById,
    getByKey,
    getByCode,
    getBySlug,
  };
}
